import React from 'react'
import PropTypes from 'prop-types'
import lang from '@/lang'

import styles from './currency-input.css'
import InputError from '@/modules/react/inputs/input-error/input-error'
import InputHint from '@/modules/react/inputs/components/react_input_hint'
import { FillTransition } from '@/modules/base/transitions/index'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import {
  sanitizeText,
  sanitizeNumber,
  formatNumber,
  formatMoney,
} from '@/modules/react/util/index'
import shortId from '@/utils/short-id'
import { ErrorMessage, FormikContext } from 'formik'
import clsx from 'clsx'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'
import { isEmpty } from 'lodash'
import Tooltip from '@mui/material/Tooltip'

// A field that is deliberately left blank - e.g. the inactive column of a two-field row - opts out
// of the 0,00 fallback via `allowEmpty`.
const isBlank = (value) => value === '' || value === null || value === undefined

class CurrencyInput extends React.Component {
  constructor(props) {
    super(props)

    const { value, autoFocus = false } = props
    let initialValue = value
    if (autoFocus) {
      const newState = sanitizeNumber(value, this.props)
      initialValue = formatNumber(newState.value, this.props)
    }

    this.state = {
      value: initialValue,
      focused: autoFocus,
    }

    this.onChange = this.onChange.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.createCurrencyValue = this.createCurrencyValue.bind(this)
    this.debounceOnFocus = true
  }

  componentDidMount() {
    const { value } = this.state
    const { min, allowEmpty } = this.props
    setTimeout(() => { this.debounceOnFocus = false }, 500)

    // setState, not setValue: a blank field must not report a 0 back through onChange.
    if (allowEmpty && isBlank(value)) {
      this.setState({ value: '' })
      return
    }

    const defaultValue = value || min || 0
    this.writeValueToInput(defaultValue)
  }

  componentDidUpdate(prevProps) {
    const { value: newValue, allowEmpty } = this.props
    const { value: prevValue, useFormik, name } = prevProps
    const { focused } = this.state

    if (allowEmpty && isBlank(newValue)) {
      if (this.state.value !== '') this.setState({ value: '' })
      return
    }

    if (!!useFormik && this.context && !focused) {
      const formattedValue = formatMoney(this.context.getFieldMeta(name).value, this.props)
      if (this.state.value !== formattedValue) {
        this.setState({ value: formattedValue })
      }
      return
    }

    if (newValue !== prevValue && !focused) {
      this.setState({ value: formatMoney(newValue, this.props) })
    }
  }

  createCurrencyValue(targetValue) {
    const { name, useFormik } = this.props
    /* eslint-disable no-extra-boolean-cast */
    const fieldMeta = !!useFormik ? this.context?.getFieldMeta(name) : null
    return fieldMeta ? fieldMeta.value : targetValue
  }

  async onChange(event) {
    const { target: { value } } = event
    const { name, useFormik } = this.props

    const newValue = sanitizeText(value, this.props)
    this.setValue(newValue)

    const helpers = !!useFormik ? this.context?.getFieldHelpers(name) : null

    if (helpers && helpers.setValue) {
      helpers.setValue(sanitizeNumber(newValue, this.props).value)
    }
  }

  onFocus(event) {
    if (this.debounceOnFocus) {
      return
    }

    const { target } = event
    const newState = sanitizeNumber(target.value, this.props)
    newState.value = formatNumber(newState.value, this.props)
    newState.focused = true

    this.setState(newState, () => { target.select() })
  }

  onBlur(event) {
    if (this.debounceOnFocus) {
      return
    }
    const { target: { value } } = event
    const { name, onBlur, useFormik } = this.props

    const field = !!useFormik ? this.context?.getFieldProps(name) : null
    if (field && field.onBlur) {
      field.onBlur(event)
    }

    this.setState({ focused: false }, () => {
      const { value: rawValue } = sanitizeNumber(value, this.props)
      this.writeValueToInput(value)
      onBlur && onBlur(rawValue, name)
    })
  }

  writeValueToInput(value) {
    const createdValue = this.createCurrencyValue(value)

    const newState = sanitizeNumber(createdValue, this.props)
    newState.value = formatMoney(newState.value, this.props)
    this.setValue(newState.value)
  }

  setValue(value) {
    const { name, onChange } = this.props
    this.setState({ value }, () => {
      const { value: rawValue } = sanitizeNumber(value, this.props)
      onChange && onChange(rawValue, name)
    })
  }

  render() {
    const { value } = this.state
    const {
      name,
      label,
      placeholder,
      readOnly,
      disabled,
      customClass,
      errorMessage,
      customNumberError,
      hintMessage,
      tooltip,
      autoFocus,
      fillAnimationOptions,
      useFormik,
      dataQsId,
      validationTooltip,
    } = this.props

    const formik = useFormik === undefined || useFormik ? this.context : null
    const fieldMeta = formik?.getFieldMeta(name)
    const isError = fieldMeta?.touched && fieldMeta?.error

    const hasValidationIndicator = validationTooltip && !isEmpty(validationTooltip)
    const validationMessage = useFormik
      ? (fieldMeta?.touched ? customNumberError ?? fieldMeta?.error ?? lang.invalidNumberErrorMessage : '')
      : (customNumberError || errorMessage || '')
    const validationClass = hasValidationIndicator
      ? (validationTooltip?.state === 'warning' ? styles.warning : styles.invalid)
      : ''
    const inputClass = clsx(
      styles.input,
      (errorMessage || isError) && styles.invalid,
      this.props.tall && styles.inputTall,
      hasValidationIndicator && styles.withValidationIndicator,
      validationClass,
    )
    const disabledClass = readOnly || disabled ? styles.disabled : ''
    const elementId = `input-${shortId()}`
    const suffixClass = `${styles.suffix} ${this.props.tall ? styles.suffixTall : ''}`
    const resolvedValidationTooltipBody = validationTooltip?.body || validationMessage
    const shouldRenderInlineError = !hasValidationIndicator

    let errorElm = null
    if (useFormik) {
      errorElm = (
        <ErrorMessage
          name={name}
          className={styles.error}
          component="div"
          render={(msg) => {
            return <div className={styles.error}>{customNumberError ?? msg ?? lang.invalidNumberErrorMessage}</div>
          }} />
      )
    } else {
      errorElm = <InputError visible={!!errorMessage} errorMessage={errorMessage} />
    }

    const labelEl = label
      ? <label className={styles.label} htmlFor={elementId}>{label}</label>
      : null

    const content = (
      <div className={clsx(styles.container, customClass, disabledClass)} data-qs-id={dataQsId}>
        {labelEl}
        <FillTransition {...fillAnimationOptions}>
          <input
            type='text'
            value={value}
            name={name}
            ref={name}
            disabled={readOnly || disabled}
            placeholder={placeholder}
            className={inputClass}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            autoFocus={autoFocus}
            id={elementId}
          />
        </FillTransition>

        <InputHint
          visible={!!hintMessage}
          hintMessage={hintMessage} />
        {shouldRenderInlineError ? errorElm : null}
        {
          this.props.suffix
            ? (
              <div className={suffixClass}>
                {this.props.suffix}
              </div>
            )
            : null
        }
        {hasValidationIndicator
          ? (
            <div className={styles.validationIndicatorWrapper}>
              <RichTooltip
                disabled={!hasValidationIndicator}
                placement={validationTooltip?.placement}
                heading={validationTooltip?.heading}
                body={resolvedValidationTooltipBody}>
                <span className={clsx(styles.validationIndicator, validationTooltip?.state === 'warning' ? styles.validationIndicatorWarning : styles.validationIndicatorError)}>
                  {validationTooltip?.state === 'warning'
                    ? <WarningAmberRoundedIcon fontSize='inherit' />
                    : <HighlightOffRoundedIcon fontSize='inherit' />}
                </span>
              </RichTooltip>
            </div>
          )
          : null}
      </div>
    )

    // Hover tooltip on the whole control (same affordance as TextInput/DateInput's
    // `tooltip` prop) — unlike hintMessage, no extra text row below the input.
    return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content
  }
}

CurrencyInput.contextType = FormikContext

CurrencyInput.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  errorMessage: PropTypes.string,
  hintMessage: PropTypes.string,
  tooltip: PropTypes.string,
  symbol: PropTypes.string,
  precision: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  decimal: PropTypes.string,
  thousand: PropTypes.string,
  format: PropTypes.string,
  disabled: PropTypes.bool,
  leftLabel: PropTypes.bool,
  hasBorder: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  fillAnimationOptions: PropTypes.shape({
    show: PropTypes.bool,
    color: PropTypes.string,
  }),
  useFormik: PropTypes.bool,
  allowEmpty: PropTypes.bool,
  customNumberError: PropTypes.string,
  dataQsId: PropTypes.string.isRequired,
  validationTooltip: PropTypes.shape({
    state: PropTypes.oneOf(['error', 'warning']),
    heading: PropTypes.string,
    body: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  }),
}

CurrencyInput.defaultProps = {
  value: 0,
  name: '',
  label: '',
  placeholder: '',
  errorMessage: '',
  hintMessage: '',
  tooltip: undefined,
  symbol: '€',
  precision: 2,
  min: NaN,
  max: NaN,
  decimal: ',',
  thousand: '',
  format: '%v %s',
  selectOnFocus: false,
  disabled: false,
  useFormik: false,
  fillAnimationOptions: {},
  showValidationIndicator: false,
}

export default CurrencyInput
